<template>
  <div class="company-page">
    <div class="company-page-loader" v-if="loading">
      <loading></loading>
    </div>

    <div class="row gutter-md" v-else>
      <!-- Left column -->
      <div
        class="col-xs-12 col-sm-6 col-md-4 offset-md-2 col-lg-offset-4 col-lg-4 col-xl-4"
      >
        <!-- Main company header -->
        <div class="company-info">
          <div class="source">
            <div class="source-icon">
              <img class="source-image" src="../../../assets/company.svg" />
            </div>
          </div>

          <h4 v-if="!!company.name" class="company-name">{{ company.name }}</h4>

          <p v-if="!!company.website" class="company-website">
            <a
              link
              class="link"
              target="_blank"
              :href="'http://' + company.company_website"
              >{{ company.company_website }}</a
            >
          </p>

          <p class="title-subtext">
            {{ $t("ui.company.created") }}
            {{ formatDateTimeNatural(company.created_at) }}
          </p>
          <p class="title-subtext">
            {{ $t("ui.company.updated") }}
            {{ formatDateTimeNatural(company.updated_at) }}
          </p>
        </div>

        <!-- Company details section -->
        <div class="company-details">
          <div class="company-details-header justify-between flex">
            <h5 class="block">{{ $t("ui.company.company_details") }}</h5>
            <a
              class="link block blue-text padding-left"
              @click="setIsOpen(true)"
            >
              {{ $t("ui.company.edit") }}</a
            >
          </div>
          <div v-for="entry in standardCompanyFields" class="info-block">
            <p v-if="entry.display_name" class="title-subtext">
              {{ $t("ui.company.caption" + "." + entry.internal_name) }}
            </p>
            <p v-if="entry.val == ''" class="body-p">-</p>
            <p v-else-if="entry.internal_name == 'country'" class="body-p">
              {{ country }}
            </p>
            <p v-else class="body-p">
              {{ entry.val }}
            </p>
          </div>
        </div>
      </div>

      <!-- Middle column -->
      <div class="col-xs-12 col-sm-6 col-md-4 col-lg-4 col-xl-4">
        <!-- Company Contacts list -->
        <div class="contact-panel" v-if="contacts.length > 0">
          <h5 class="contact-title">
            {{ $t("ui.company.contacts") }}
            ({{ this.totalContacts }})
          </h5>

          <div
            :key="'contact-' + contact.id"
            v-for="contact in contacts"
            class="company-wrapper"
          >
            <q-card-title class="contact-card">
              <user-image
                :firstname="contact.firstname"
                :lastname="contact.lastname"
                :email="contact ? contact.email : ''"
                size="small"
              />

              <div class="contact-info" v-if="contact.name">
                <p class="contact-title-label">{{ contact.name }}</p>
                <p class="caption contact-title-sublabel"
                  slot="subtitle"
                  v-if="contact && contact.email">
                  {{ contact.email }}
                </p>


                <p
                  slot="subtitle"
                  class="contact-title-sublabel missing-info"
                  v-else
                >
                  {{ $t("ui.company.missing.email") }}
                </p>
              </div>

              <div
                class="contact-info"
                v-else-if="contact.company && contact.email"
              >
                <p class="contact-title-label">
                  {{ contact.email }}
                </p>

                <p
                  slot="subtitle"
                  class="contact-title-sublabel missing-info"
                >
                  {{ $t("ui.company.missing.name") }}
                </p>
              </div>

              <div class="contact-info" v-else>
                <p class="contact-title-label">
                  {{ $t("ui.company.missing.headline") }}
                </p>

                <p
                  slot="subtitle"
                  class="contact-title-sublabel missing-info"
                >
                  {{ $t("ui.company.missing.email_name") }}
                </p>
              </div>

              <q-icon slot="right" name="more_horiz">
                <q-popover class="contact-actions-popover">
                  <q-list link class="no-border">
                    <q-item v-close-overlay>
                      <q-item-main
                        @click.native="goToContact(contact.id)"
                        :label="$t('ui.company.contact.go_to_contact')"
                        class="contact-action-item"
                      />
                    </q-item>
                  </q-list>
                </q-popover>
              </q-icon>
            </q-card-title>
          </div>

          <div class="contacts-link">
            <a class="link" @click="goToContacts()">
              {{ $t("ui.company.contact.view_all") }}
            </a>
          </div>
        </div>

        <!-- Empty state if contacts arent available  -->
        <div v-else>
          <empty-state
            name="collaborators"
            :subtitle="$t('ui.company.empty_state_text')"
          ></empty-state>
        </div>
      </div>
    </div>

    <company-edit
      :isOpen="isOpen"
      :company="company"
      @cancel="setIsOpen(false)"
      @save="updateCompany"
    ></company-edit>

    <!-- Delete company modal -->
    <modal
      class="delete-modal"
      :open="deleteModalOpen"
      :title="$t('ui.company.modals.delete.title')"
      :subtitle="deleteCompanySubtitle"
      type="warning"
      :showCancelForWarning="true"
      :saveText="$t('ui.company.modals.delete.confirm')"
      :saveDisabled="deleteCompanyNameConfirm != $t('ui.company.modals.delete.confirm_word')"
      @save="deleteCompany"
      @cancel="openDeleteModal(false)"
    >
      <field
        v-model="deleteCompanyNameConfirm"
        type="text"
        :label="$t('ui.company.modals.delete.label')"
      />
    </modal>
  </div>
</template>

<script>
import CompanyApi from "@/api/Company";
import ContactsApi from "@/api/Contacts";

import Ui from "@/classes/Ui";
import Countries from "@/config/data/countries";

import ContactInitials from "@/components/helpers/DataDisplay/ContactInitials";
import UserImage from "@/components/helpers/Specific/UserImage";
import Loading from "@/components/helpers/Specific/Loading.vue";
import CompanyEdit from "@/components/pages/companies/CompanyEdit.vue";
import EmptyState from "@/components/helpers/DataDisplay/EmptyStateContacts";
import Modal from "@/components/helpers/Layout/NewModal"
import Field from "@/components/helpers/DataInput/Field"

export default {
  components: {
    EmptyState,
    Loading,
    ContactInitials,
    UserImage,
    CompanyEdit,
    Modal,
    Field,
  },
  props: {
    id: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      company: {},
      totalContacts: 0,
      isOpen: false,
      deleteModalOpen: false,
      deleteCompanyNameConfirm: '',
      standardCompanyFields: [
        { internal_name: "name", display_name: "Company", val: "" },
        { internal_name: "street", display_name: "Street", val: "" },
        { internal_name: "street_number", display_name: "Street Number", val: "" },
        { internal_name: "city", display_name: "City", val: "" },
        { internal_name: "zip", display_name: "Post Code", val: "" },
        { internal_name: "country", display_name: "Country", val: "" }
      ],
      filters: {
        group: {
          merge: "",
          conditions: [
            {
              attribute: "company_id",
              predicate: "eq",
              value: "",
              value_id: null,
              definition: {
                label: "Company",
                attribute: "company_id",
                match_by_id: true,
                type: "text"
              }
            }
          ]
        }
      },
      contacts: [],
      loading: true
    };
  },
  computed: {
    // eslint-disable-next-line vue/return-in-computed-property
    emptyState() {
      return this.$store.getters.getEmptyState;
    },
    userLanguage() {
      return this.$store.getters.getCurrentLanguage;
    },
    country() {
      if (this.company.address && this.company.address.country) {
        let countryCode = this.company.address.country.toUpperCase();
        let code = "";
        let language = this.userLanguage;
        Countries[language].forEach(country => {
          if (countryCode == country.value) {
            code = country.name;
          }
        });
        return code;
      }
    },
    deleteCompanySubtitle() {
      let subtitle = this.$t('ui.company.modals.delete.subtitle', { companyName: this.company.name })
      if (this.totalContacts > 0) {
        let contacsCount = this.$tc('ui.contacts.enumeration', this.totalContacts, { n: this.totalContacts })
        subtitle += '. ' + this.$t('ui.company.modals.delete.warning', { things: contacsCount })
      }   
      return subtitle
    }
  },
  beforeMount() {
    Ui.setSecondaryHeaderTabs({})
    this.setHeaderButtons()

    Ui.setBreadcrumbs([
      {
        label: "ui.company.breadcrumb_back",
        link: { name: "companies" }
      }
    ]);
  },
  async mounted() {
    try {
      await this.getCompany(this.id)
      this.filters.group.conditions[0].value = this.company.name
      this.filters.group.conditions[0].value_id = this.company.id
      await this.getContacts(this.id);

      Ui.setTitle({ label: this.company.name })
    } finally {
      // Switch from the loader to the main page
      this.loading = false;
    }
  },
  methods: {
    setHeaderButtons() {
      Ui.setHeaderButtons([
        {
          action: "companies.delete",
          classes: "btn-delete",
          function: this.openDeleteModal
        }
      ])
    },
    async getCompany(id) {
      // Get the basic details for this contact
      let companyApi = new CompanyApi();
      this.company = await companyApi.getCompany(id);

      Object.keys(this.company).forEach(entry => {
        this.standardCompanyFields.forEach(field => {
          if (field.internal_name == entry) {
            field.val = this.company[entry];
          }
        });
      });

      // Add to our array of standard fields the matching values for Company Address
      if (
        typeof this.company.address == "object" &&
        this.company.address !== null
      ) {
        Object.keys(this.company.address).forEach(entry => {
          this.standardCompanyFields.forEach(field => {
            if (field.internal_name == entry) {
              field.val = this.company.address[entry];
            }
          });
        });
      }
    },
    async getContacts() {
      let contactApi = new ContactsApi();

      let filters = _.cloneDeep(this.filters);
      // TODO: It would probably be better for the API to do this.
      filters.group.conditions[0].value = filters.group.conditions[0].value_id;

      let request = {
        search: this.searchKeywords,
        contact_filters: filters,
        page: 1,
        per_page: 10,
        sort_by: "created_at",
        sort_order: "DESC"
      };

      this.loading = true;

      // Make the request
      let requestResponse = null;
      try {
        requestResponse = await contactApi.getAllContacts(request);
      } finally {
        this.loading = false;
        this.$emit("fetchDataComplete");
      }

      // Display both the returned contacts page and the total number of contacts
      this.contacts = requestResponse.data.resources;
      this.totalContacts = requestResponse.data.total_results;
    },
    async updateCompany(company) {

      this.isOpen = false

      if (JSON.stringify(company) == JSON.stringify(this.company)) {
        return
      }

      let api = new CompanyApi();
      let res = await api
        .updateCompany(this.company.id, company)
        .then(rs => {
          this.company = rs;
          Ui.setTitle({ label: this.company.name });
        });

      Object.keys(this.company).forEach(entry => {
        this.standardCompanyFields.forEach(field => {
          if (field.internal_name == entry) {
            field.val = this.company[entry];
          }
        });
      });

      // Add to our array of standard fields the matching values for Company Address
      if (
        typeof this.company.address == "object" &&
        this.company.address !== null
      ) {
        Object.keys(this.company.address).forEach(entry => {
          this.standardCompanyFields.forEach(field => {
            if (field.internal_name == entry) {
              field.val = this.company.address[entry];
            }
          });
        });
      }

      this.key++;
    },
    setIsOpen(bool) {
      this.isOpen = bool;
    },
    openDeleteModal(isOpen = true) {
      this.deleteModalOpen = isOpen
      this.deleteCompanyNameConfirm = ''
    },
    async deleteCompany() {
      if (this.deleteCompanyNameConfirm === this.$t('ui.company.modals.delete.confirm_word')) { 
        let api = new CompanyApi()
        let id = this.company.id
        if (id) {
          let status = await api.deleteCompany(id)
          if (status == 204) {
            this.$router.push('/companies')
          }
        }
      }

      this.openDeleteModal(false)
    },
    goToContact(contactId) {
      this.$router.push({
        path: `/contact/${contactId}`
      });
    },
    goToContacts() {
      this.$router.push({
        name: "contacts",
        params: {
          filtersFromParams: this.filters
        }
      });
    }
  }
};
</script>

<style lang="sass">
@media (max-width: 575px)
  .edit-company-modal
    max-height: 500px !important
    .modal-wrapper
      min-width: 100%
      width: 320px
    .modal-component
      padding: 1em 0 !important
      text-align: center !important
</style>

<style lang="sass">
.contact-actions-popover
  box-shadow: 0 20px 66px 0 rgba(34,48,73,.2) !important
  margin-top: 5px
  .q-list
    padding: 5px 0px
    .q-item
      font-size: 14px
      font-weight: $font-medium
      color: $ink-light
      padding: 8px 30px
      .q-item-section
        .q-item-label
          padding: 0px
      &.q-select-highlight
        background: $paper-lighter !important
        color: $ink !important
      &:hover
        background: $paper-lighter

.company-page
  .link
    cursor: pointer

  .block
    display: block
    padding: 0px

  .blue-text
    margin: 0px
    margin-top: 7px
    cursor: pointer

  .company-details
    padding: 20px 30px 20px 30px
    background: $paper-lighter
    border-radius: 5px
    line-height: 20px
    margin-bottom: 20px
    margin-top: 15px
    h5, h4
      margin: 0px 0px 8px 0px
      color: $ink
    .company-details-header
      a
        margin: 0

  .company-page-loader
    z-index: 200
    position: absolute
    top: 0
    left: 0
    margin: 0 auto
    width: 100%
    height: 100%

  .q-item-label
    padding: 20px 20px 30px 0px

  .company-info
    text-align: center
    padding: 0px 15px 20px 15px

    .company-website
      color: $ink-lighter
      margin-bottom: 20px !important

    .source
      text-align: center
      display: flex
      align-items: flex-end
      justify-content: center
      margin-top: 20px

      .source-image
        width: 100px
        height: 100px

      .source-icon
      .desc
        padding-left: 15px
        p.body-p
          color: $ink-light
          margin-bottom: 0px !important
    h4
      margin: 20px 0px 0px 0px

    .title-subtext
      font-size: 12px
      line-height: 16px
      margin-bottom: 0px
      font-weight: $font-medium !important
      color: $ink-lighter

  .company-wrapper
    margin-bottom: 20px

  .info-block
    padding: 8px 0px

    .title-subtext
      font-size: 12px
      line-height: 16px
      margin-bottom: 0px
      font-weight: $font-medium !important
      color: $ink-lighter
      margin-bottom: 6px

    p.body-p
      color: $ink
      margin: 0px
      font-weight: 500

  .contact-card
    background: $paper-lighter
    padding: 16px 30px
    padding: 0px 20px
    height: 72px
    display: flex
    align-items: center
    border-radius: 5px
    .q-card-title
      display: flex
      align-items: center

    .q-card-title-extra
      background: $paper-light
      padding: 1px 3px
      height: 32px
      width: 32px
      border-radius: 3px
      align-items: center
      justify-content: center
      display: flex
      &:hover
        i
          color: $ink
      &:active
        background: $sensual-lighter !important
        i
          color: $sensual !important
      i
        color: $ink-lighter


    .contact-title-label
      margin-bottom: 0

    .contact-title-sublabel
      margin: 0

      &.missing-info
        color: $red !important
        font-size: 12px

    .circle
      float: left
      margin-right: 10px
      margin-top: 0

  .contact-panel
  .delete-modal
    .field
      width: 100%
      .contacts-link
      .contact-title
        margin-top: 0px
        margin-bottom: 10px

</style>



// WEBPACK FOOTER //
// Company.vue?b3fae18a